<template>
  <v-data-table
    :headers="headers"
    :items="filteredItems"
    class="elevation-1"
    :loading="isLoading"
    :search="search"
    :item-class="itemClass"
  >
    <template v-slot:top="">
      <v-row style="padding: 10px">
        <v-col cols="3">
          <vue-excel-xlsx
            :data="filteredItems"
            :columns="columns"
            :file-name="'Processing Data'"
            :file-type="'xlsx'"
            :sheet-name="'sheetname'"
            >
              <v-btn color="success">
                <v-icon class="mr-2">{{ mdiMicrosoftExcel}}</v-icon>
                Export</v-btn>
          </vue-excel-xlsx>
        </v-col>
        <v-col cols="3">
          <v-select
            clearable
            :items="stockTypes"
            v-model="filterStockType"
            label="Filter Types"
          ></v-select>
        </v-col>
        <v-col cols="3">
          <v-select
            clearable
            :items="farms"
            v-model="filterFarm"
            label="Filter Farm"
            item-value="id"
            item-text="name"
          ></v-select>
        </v-col>
        <v-col cols="3">
          <v-text-field
            name="name"
            label="Search"
            v-model="search"
            id="id"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
    <template v-slot:item.meatMass="{ item }">
      <span v-if="item.meatMass">
        {{ item.meatMass.toFixed(3) }}
      </span>
    </template>
    <template v-slot:item.purgingLoss="{ item }">
      <span v-if="item.purgingLoss">
        {{ item.purgingLoss }}%
      </span>
    </template>
    <template v-slot:item.dripLoss="{ item }">
      <span v-if="item.dripLoss">
        {{ item.dripLoss }}%
      </span>
    </template>
  </v-data-table>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronRight,
  mdiChevronUp,
  mdiCheckCircleOutline,
  mdiFileDocument,
  mdiCheckAll,
  mdiCallSplit,
  mdiMicrosoftExcel,
} from '@mdi/js';
import { uniq } from 'lodash';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import formatting from '../mixins/formatting';
import reportCalculations from '../mixins/reportCalculations';

export default {
  name: 'ListProcessingData',
  module: 'FactoryItem',
  mixins: [formatting, loading, reportCalculations],
  data() {
    return {
      /**
      * icons
      */
      mdiArrowRight,
      mdiChevronDown,
      mdiChevronRight,
      mdiChevronUp,
      mdiCheckCircleOutline,
      mdiFileDocument,
      mdiCheckAll,
      mdiCallSplit,
      mdiMicrosoftExcel,
      /**
      * variables
      */
      expanded: [],
      filterStockType: undefined,
      filterFarm: undefined,
      search: '',
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('stockItems', ['processingStockItems']),
    /**
    * Excel column headers
    */
    columns() {
      return this.headers.map((h) => ({
        label: h.text,
        field: h.value,
      }));
    },
    /**
    * Data table column headers
    */
    headers() {
      return [
        {
          text: this.$$t('productionCode'),
          value: 'formattedProductionCode',
        },
        {
          text: this.$$t('stockType'),
          value: 'stockType',
        },
        {
          text: this.$$t('receivingDate'),
          value: 'receivingDate',
        },
        {
          text: 'Source',
          value: 'deliveryNote.farm.name',
        },
        {
          text: 'Farm Delivery Note Number',
          value: 'deliveryNote.farmDeliveryNoteNumber',
        },
        {
          text: 'Source Harvested Mass (kg)',
          value: 'harvestedMass',
        },
        {
          text: 'Source Packed Mass (kg)',
          value: 'packedMass',
        },
        {
          text: 'Source Number of Abalone',
          value: 'quantity',
        },
        {
          text: 'Purging Loss',
          value: 'purgingLoss',
        },
        {
          text: 'Factory GRN',
          value: 'deliveryNote.goodsReceivedNumber',
        },
        {
          text: this.$$t('productionDate'),
          value: 'productionDate',
        },
        {
          text: 'Factory intake mass (kg)',
          value: 'receivedMass',
        },
        {
          text: 'Driploss %',
          value: 'dripLoss',
        },
        {
          text: 'Factory processed mass (kg)',
          value: 'processingMass',
        },
        {
          text: 'Intake temperature (°C)',
          value: 'receivedTemp',
        },
        {
          text: 'Mass before trimming (kg)',
          value: '',
        },
        {
          text: 'Meat mass (kg)',
          value: 'meatMass',
        },
        {
          text: '',
          value: '',
        },
        {
          text: this.$$t('processingMass'),
          value: 'processingMass',
        },
      ];
    },
    factoryItems() {
      let { processingStockItems } = this;
      if (!processingStockItems) {
        processingStockItems = [];
      }
      const factoryItems = processingStockItems.map((s) => {
        const result = { ...s };
        const {
          deliveryNote, productionDate,
        } = result;
        // eslint-disable-next-line max-len
        result.receivedMass = s.receivedMass?.toFixed(3) ?? 0;
        result.processingMass = s.processingMass?.toFixed(3) ?? 0;
        result.formattedProductionCode = this.formatProductionCode(result);
        result.receivingDate = this.formatDate(deliveryNote?.receivingDate ?? '');
        result.productionDate = this.formatDate(productionDate);
        if (result.harvestedMass && result.packedMass) {
          result.purgingLoss = this.calcPurgingLoss(result).toFixed(2);
        }
        if (result.packedMass && result.receivedMass) {
          result.dripLoss = this.calcDripLoss(result).toFixed(2);
        }
        if (result.processCheckSheets.length > 0) {
          result.meatMass = result.processCheckSheets[0]?.meatMass;
        }
        switch (result.stockType) {
          case 'Canned':
            result.outOfSaltWeight = result.outOfSaltWeightCans;
            break;
          case 'Dried':
            result.outOfSaltWeight = result.outOfSaltWeightDried;
            break;
          case 'Pouched':
            result.outOfSaltWeight = result.outOfSaltWeightPouch;
            break;
          default:
            result.outOfSaltWeight = result.outOfSaltWeightCans;
            break;
        }
        if (s.sizeMin || s.sizeMax) {
          result.sizeRange = `${s.sizeMin} - ${s.sizeMax}`;
        }
        return result;
      });
      return factoryItems
        .filter((f) => f.state !== 'Processing-Split')
        .map((f) => {
          const result = { ...f };
          result.children = factoryItems.filter((i) => i.parentStockItemId === f.id) ?? [];
          return result;
        });
    },
    filteredItems() {
      const { factoryItems, filterFarm, filterStockType } = this;
      let result = [...factoryItems];
      if (filterStockType) {
        result = result.filter((r) => r.stockType === filterStockType);
      }
      if (filterFarm) {
        result = result.filter((r) => r.deliveryNote.farm.id === filterFarm);
      }
      return result;
    },
    farms() {
      const { factoryItems } = this;
      const farms = factoryItems
        .map((f) => f.deliveryNote.farm);
      return uniq(farms);
    },
    stockTypes() {
      const { factoryItems } = this;
      const stockTypes = factoryItems
        .map((f) => f.stockType)
        .filter((f) => f !== null);
      return uniq(stockTypes);
    },
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('stockItems', ['LOAD_processingStockItems']),
    childrenFilter(child) {
      return child.value !== 'checks' && child.value !== 'actions';
    },
    async refresh() {
      this.isLoading = true;
      await this.LOAD_processingStockItems();
      this.isLoading = false;
    },
    itemClass(item) {
      if (item.stockType === 'Canned') {
        return 'highlight-row';
      }
      return '';
    },
  },
};
</script>

<docs>
# FactoryItem example

'''js
<FactoryItem />
'''
</docs>

<style lang="scss">
.expanded-item-child {
  border-top: 1px solid var(--v-primary-base);
}
.expanded-item-container {
  /* height: 100%; */
}
</style>
